import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { fmtEur, fmtDate } from '../../lib/utils';
import { toast } from '../../stores/toast.store';
import { celebrate } from '../../stores/moment.store';
import { confirm as confirmDialog } from '../../stores/confirm.store';
import type { IgnoreReasonKey, MatchKind, PayMethod, UscitaSource } from './adminApi';

/**
 * Azioni condivise dell'Amministrazione (tab, stream Oggi, modali).
 *
 * Stesso pattern di useDocActions: ogni mutation invalida ['admin'] (tutte le
 * viste leggono gli stessi aggregati: overview, incassi, uscite, movimenti),
 * gestisce toast/undo/celebrate; i consumer chiamano solo `.mutate(...)` e
 * usano `.isPending` per il disable.
 */

export interface MarkPaidArgs {
  paymentId: string;
  amount: number;
  label?: string;          // "Fattura 2026/12 · La Rosa" per il toast
  paidDate: string;        // YYYY-MM-DD
  method: PayMethod;
  accountId?: string | null;
  bankTransactionId?: string | null;
  /** Perché la si chiude qui: finisce nella tab "Chiuse qui". */
  localPaidNote?: string | null;
  /** Chiamata dopo un Annulla riuscito (lo stream Oggi rimette la riga al suo posto). */
  onUndone?: () => void;
}

export interface PayArgs {
  source: UscitaSource;
  id: string;
  amount: number;
  label?: string;
  paidDate: string;
  method: PayMethod;
  accountId?: string | null;
  bankTransactionId?: string | null;
  /** Ricorrenti: quale occorrenza si sta pagando (senza, l'API prende la prossima). */
  dueDate?: string | null;
  /** Solo PAYABLE: perché la rata si chiude qui e non su FiC. */
  localPaidNote?: string | null;
}

/** Che documento si sta cestinando: fattura emessa o fattura ricevuta. */
export type DocTrashKind = 'invoice' | 'payable';

export interface IgnoreDocsArgs {
  kind: DocTrashKind;
  /** Id dei DOCUMENTI (non delle rate). */
  ids: string[];
  reason: IgnoreReasonKey;
  note?: string | null;
  /** "2026/57 · Milana Roberta" per il toast. */
  label?: string;
}

const kindRestorePath = (kind: DocTrashKind) => (kind === 'payable' ? '/admin/payables/restore' : '/admin/invoices/restore');

export function useAdminActions() {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin'] });
    // Le fatture di Contabilità cambiano stato quando l'ultima rata è incassata
    qc.invalidateQueries({ queryKey: ['invoices'] });
  };

  /**
   * Incassa una rata: celebrazione + Annulla (unmark) per 6 secondi.
   *
   * L'incasso si registra SOLO qui: su FattureInCloud non si scrive mai (niente
   * `syncToFic` nel body, e lato API il default è false). La rata resta chiusa
   * anche dopo il sync FiC e compare nella tab "Chiuse qui" finché FiC non la
   * recupera.
   */
  const markPaid = useMutation({
    mutationFn: ({ paymentId, paidDate, method, accountId, bankTransactionId, localPaidNote }: MarkPaidArgs) =>
      api.post(`/admin/incassi/${paymentId}/mark-paid`, {
        paidDate, method,
        accountId: accountId || undefined,
        bankTransactionId: bankTransactionId || undefined,
        localPaidNote: localPaidNote || undefined,
      }),
    onSuccess: (_r, vars) => {
      invalidate();
      celebrate({ title: 'Incassato', amount: vars.amount, tone: 'ok' });
      toast.undo('Incasso registrato', {
        description: `${vars.label ? `${vars.label} · ` : ''}€ ${fmtEur(vars.amount)} il ${fmtDate(vars.paidDate)}`,
        onUndo: () => {
          api.post(`/admin/incassi/${vars.paymentId}/unmark`)
            .then(() => { invalidate(); vars.onUndone?.(); })
            .catch((e: any) => toast.alert('Annullamento fallito', e?.message ?? 'Riprova'));
        },
      });
    },
    onError: (e: any) => toast.alert('Incasso non registrato', e?.message ?? 'Riprova'),
  });

  /** Riporta una rata a "da incassare" (azione esplicita dalla tab Incassate). */
  const unmarkPaid = useMutation({
    mutationFn: ({ paymentId }: { paymentId: string; label?: string }) => api.post(`/admin/incassi/${paymentId}/unmark`),
    onSuccess: (_r, vars) => { invalidate(); toast.ok('Rata riaperta', vars.label); },
    onError: (e: any) => toast.alert('Rata non riaperta', e?.message ?? 'Riprova'),
  });

  /** Paga un'uscita (fornitore, ricorrente, tassa o contanti). */
  const pay = useMutation({
    mutationFn: ({ source, id, paidDate, method, accountId, bankTransactionId, dueDate, localPaidNote }: PayArgs) =>
      api.post('/admin/uscite/pay', {
        source, id, paidDate, method,
        dueDate: source === 'RECURRING' && dueDate ? dueDate : undefined,
        accountId: accountId || undefined,
        bankTransactionId: bankTransactionId || undefined,
        localPaidNote: localPaidNote || undefined,
      }),
    onSuccess: (_r, vars) => {
      invalidate();
      toast.ok('Pagamento registrato', `${vars.label ? `${vars.label} · ` : ''}€ ${fmtEur(vars.amount)} il ${fmtDate(vars.paidDate)}`);
    },
    onError: (e: any) => toast.alert('Pagamento non registrato', e?.message ?? 'Riprova'),
  });

  /**
   * Abbina un movimento Qonto a una rata / fattura ricevuta / rata tassa / voce
   * contanti. Con markPaid (default) la voce viene anche segnata incassata o
   * pagata alla data valuta del movimento: abbinare = registrare.
   */
  const match = useMutation({
    mutationFn: ({ transactionId, kind, id, markPaid }: { transactionId: string; kind: MatchKind; id: string; label?: string; markPaid?: boolean }) =>
      api.post(`/admin/transactions/${transactionId}/match`, { kind, id, markPaid: markPaid ?? true }),
    onSuccess: (_r, vars) => { invalidate(); toast.ok('Movimento abbinato', vars.label); },
    onError: (e: any) => toast.alert('Abbinamento non riuscito', e?.message ?? 'Riprova'),
  });

  const unmatch = useMutation({
    mutationFn: ({ transactionId }: { transactionId: string; label?: string }) => api.delete(`/admin/transactions/${transactionId}/match`),
    onSuccess: (_r, vars) => { invalidate(); toast.ok('Movimento scollegato', vars.label); },
    onError: (e: any) => toast.alert('Scollegamento non riuscito', e?.message ?? 'Riprova'),
  });

  /** Aggiorna i saldi Qonto (conti + movimenti). Toast con i numeri di `stats` dell'API. */
  const syncQonto = useMutation({
    mutationFn: () => api.post<{ ok?: boolean; stats?: { accounts?: number; created?: number; updated?: number; taxMatched?: number } } | undefined>('/admin/sync/qonto'),
    onSuccess: (r) => {
      invalidate();
      qc.invalidateQueries({ queryKey: ['qonto-status'] });
      const s = r?.stats;
      const bits: string[] = [];
      if (s?.created != null) bits.push(`${s.created} movimenti nuovi`);
      if (s?.updated != null) bits.push(`${s.updated} aggiornati`);
      if (s?.taxMatched) bits.push(`${s.taxMatched} F24 abbinati`);
      toast.ok('Qonto aggiornato', bits.join(' · ') || undefined);
    },
    onError: (e: any) => toast.alert('Aggiornamento Qonto fallito', e?.message ?? 'Riprova'),
  });

  /** Import completo da FattureInCloud (~25 s): fatture, rate, ricevute, tasse. */
  const syncFic = useMutation({
    mutationFn: () => api.post<{ ok?: boolean; stats?: { invoices?: number; invoicePayments?: number; payables?: number; taxes?: number } } | undefined>('/admin/sync/fic'),
    onSuccess: (r) => {
      invalidate();
      qc.invalidateQueries({ queryKey: ['fic-status'] });
      const s = r?.stats;
      const bits: string[] = [];
      if (s?.invoices != null) bits.push(`${s.invoices} fatture`);
      if (s?.invoicePayments != null) bits.push(`${s.invoicePayments} rate`);
      if (s?.payables != null) bits.push(`${s.payables} ricevute`);
      toast.ok('FattureInCloud aggiornato', bits.join(' · ') || undefined);
    },
    onError: (e: any) => toast.alert('Aggiornamento FiC fallito', e?.message ?? 'Riprova'),
  });

  /** PATCH generico su una risorsa admin (tasse, ricorrenti, fatture ricevute, conti). */
  const patch = useMutation({
    mutationFn: ({ path, data }: { path: string; data: Record<string, unknown>; okTitle?: string }) => api.patch(path, data),
    onSuccess: (_r, vars) => { invalidate(); if (vars.okTitle) toast.ok(vars.okTitle); },
    onError: (e: any) => toast.alert('Modifica non salvata', e?.message ?? 'Riprova'),
  });

  /**
   * Cestina uno o più documenti: spariscono da liste e totali, restano nel
   * cestino (?status=cestinate) con motivo e data. Non tocca FattureInCloud e
   * non cancella niente dal database, quindi l'Annulla del toast è un ripristino
   * vero e proprio, non una pezza.
   */
  const ignoreDocs = useMutation({
    mutationFn: ({ kind, ids, reason, note }: IgnoreDocsArgs) =>
      api.post(kind === 'payable' ? '/admin/payables/ignore' : '/admin/invoices/ignore', { ids, reason, note: note || undefined }),
    onSuccess: (_r, vars) => {
      invalidate();
      toast.undo(vars.ids.length > 1 ? `${vars.ids.length} documenti cestinati` : 'Documento cestinato', {
        description: vars.label,
        onUndo: () => {
          api.post(kindRestorePath(vars.kind), { ids: vars.ids })
            .then(() => { invalidate(); toast.ok('Ripristinato'); })
            .catch((e: any) => toast.alert('Ripristino fallito', e?.message ?? 'Riprova'));
        },
      });
    },
    onError: (e: any) => toast.alert('Non cestinato', e?.message ?? 'Riprova'),
  });

  /** Rimette in circolo un documento dal cestino (tasto Ripristina della lista). */
  const restoreDocs = useMutation({
    mutationFn: ({ kind, ids }: { kind: DocTrashKind; ids: string[]; label?: string }) =>
      api.post(kindRestorePath(kind), { ids }),
    onSuccess: (_r, vars) => { invalidate(); toast.ok('Ripristinato', vars.label); },
    onError: (e: any) => toast.alert('Ripristino fallito', e?.message ?? 'Riprova'),
  });

  // ── Riconciliazione banca ↔ documenti ───────────────────────────────────

  /**
   * Applica in automatico SOLO i casi certi e aggancia i movimenti delle rate
   * già pagate. Torna il prima e il dopo, e il numero del lotto con cui si
   * torna indietro: la riga "Annulla" del toast fa esattamente quello.
   */
  const applicaRiconciliazione = useMutation({
    mutationFn: () => api.post<{ lotto: string; chiuse: number; valore: number; agganciate: number; prima: { totaleAperto: number }; dopo: { totaleAperto: number } }>('/admin/riconciliazione/applica', {}),
    onSuccess: (r) => {
      invalidate();
      const differenza = Math.round((r.prima.totaleAperto - r.dopo.totaleAperto) * 100) / 100;
      toast.undo(r.chiuse ? `${r.chiuse} rate chiuse` : 'Nessuna rata da chiudere', {
        description: `€ ${fmtEur(r.valore)} saldati · ${r.agganciate} movimenti agganciati · aperto da € ${fmtEur(r.prima.totaleAperto)} a € ${fmtEur(r.dopo.totaleAperto)} (${differenza >= 0 ? '−' : '+'} € ${fmtEur(Math.abs(differenza))})`,
        onUndo: () => {
          api.post('/admin/riconciliazione/annulla', { lotto: r.lotto })
            .then(() => { invalidate(); toast.ok('Riconciliazione annullata', 'Tutto è tornato com\'era'); })
            .catch((e: any) => toast.alert('Annullamento fallito', e?.message ?? 'Riprova'));
        },
      });
    },
    onError: (e: any) => toast.alert('Riconciliazione non riuscita', e?.message ?? 'Riprova'),
  });

  /** Conferma una singola proposta: si comporta come il pulsante "Segna incassata". */
  const confermaProposta = useMutation({
    mutationFn: (v: { lato: 'INCASSO' | 'USCITA'; rataId: string; txId: string; paidDate: string; label?: string; importo?: number }) =>
      api.post('/admin/riconciliazione/conferma', { lato: v.lato, rataId: v.rataId, txId: v.txId, paidDate: v.paidDate }),
    onSuccess: (_r, v) => {
      invalidate();
      toast.ok(v.lato === 'INCASSO' ? 'Incasso registrato' : 'Pagamento registrato', `${v.label ?? ''}${v.importo != null ? ` · € ${fmtEur(v.importo)}` : ''}`);
    },
    onError: (e: any) => toast.alert('Non registrato', e?.message ?? 'Riprova'),
  });

  /** "Questa proposta è sbagliata": la coppia rata+movimento non si ripropone più. */
  const scartaProposta = useMutation({
    mutationFn: (v: { rataId: string; txId: string; label?: string }) => api.post('/admin/riconciliazione/scarta', { rataId: v.rataId, txId: v.txId }),
    onSuccess: (_r, v) => {
      invalidate();
      toast.undo('Proposta scartata', {
        description: v.label,
        onUndo: () => {
          api.post('/admin/riconciliazione/ripristina-scarto', { rataId: v.rataId, txId: v.txId })
            .then(() => invalidate())
            .catch((e: any) => toast.alert('Ripristino fallito', e?.message ?? 'Riprova'));
        },
      });
    },
    onError: (e: any) => toast.alert('Non scartata', e?.message ?? 'Riprova'),
  });

  const ripristinaScarto = useMutation({
    mutationFn: (v: { rataId: string; txId: string }) => api.post('/admin/riconciliazione/ripristina-scarto', { rataId: v.rataId, txId: v.txId }),
    onSuccess: () => { invalidate(); toast.ok('Rimessa in lista'); },
    onError: (e: any) => toast.alert('Non ripristinata', e?.message ?? 'Riprova'),
  });

  /** Crea le uscite mancanti di una voce ricorrente (le rate vere delle auto). */
  const creaRateMancanti = useMutation({
    mutationFn: async (v: { recurringId: string; nome: string; addebiti: number; totale: number }) => {
      const ok = await confirmDialog({
        title: `Registrare ${v.addebiti} addebiti di ${v.nome}?`,
        body: `Sono addebiti veri sul conto, per € ${fmtEur(v.totale)}, che oggi non risultano da nessuna parte. Diventano uscite già pagate, con la data e l'importo del movimento. Si può annullare.`,
        confirmLabel: 'Registra',
        icon: 'repeat',
      });
      if (!ok) return null;
      return api.post<{ create: number; totale: number; lotto: string }>('/admin/riconciliazione/rate-mancanti/crea', { recurringId: v.recurringId });
    },
    onSuccess: (r) => {
      if (!r) return;
      invalidate();
      toast.undo(`${r.create} uscite registrate`, {
        description: `€ ${fmtEur(r.totale)}`,
        onUndo: () => {
          api.post('/admin/riconciliazione/rate-mancanti/annulla', { lotto: r.lotto })
            .then(() => { invalidate(); toast.ok('Annullato'); })
            .catch((e: any) => toast.alert('Annullamento fallito', e?.message ?? 'Riprova'));
        },
      });
    },
    onError: (e: any) => toast.alert('Non registrate', e?.message ?? 'Riprova'),
  });

  /** Annulla un lotto di riconciliazione già eseguito (dalla lista in fondo alla tab). */
  const annullaLotto = useMutation({
    mutationFn: async (v: { lotto: string; chiuse: number }) => {
      const ok = await confirmDialog({
        title: 'Annullare questa riconciliazione?',
        body: `${v.chiuse} rate tornano come erano prima, con la loro data e il loro conto. I movimenti si staccano.`,
        confirmLabel: 'Annulla tutto',
        icon: 'undo',
        danger: true,
      });
      if (!ok) return null;
      return api.post('/admin/riconciliazione/annulla', { lotto: v.lotto });
    },
    onSuccess: (r) => { if (!r) return; invalidate(); toast.ok('Riconciliazione annullata'); },
    onError: (e: any) => toast.alert('Annullamento fallito', e?.message ?? 'Riprova'),
  });

  /** DELETE con conferma. Ritorna null se l'utente annulla. */
  const remove = useMutation({
    mutationFn: async ({ path, title, body }: { path: string; title: string; body?: string; okTitle?: string }) => {
      const ok = await confirmDialog({ title, body, danger: true, confirmLabel: 'Elimina', icon: 'trash' });
      if (!ok) return null;
      await api.delete(path);
      return true;
    },
    onSuccess: (done, vars) => { if (!done) return; invalidate(); toast.ok(vars.okTitle ?? 'Eliminato'); },
    onError: (e: any) => toast.alert('Eliminazione non riuscita', e?.message ?? 'Riprova'),
  });

  return {
    markPaid, unmarkPaid, pay, match, unmatch, syncQonto, syncFic, patch, remove, ignoreDocs, restoreDocs, invalidate,
    applicaRiconciliazione, confermaProposta, scartaProposta, ripristinaScarto, creaRateMancanti, annullaLotto,
  };
}
